import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { useApp } from "../../context/AppContext";
import attendanceService from "../../services/attendanceService";
import courseService from "../../services/courseService";
import { getYearOptions } from "../../utils/courseYears";
import SubjectAttendanceReport from "./SubjectAttendanceReport";
import CollegePrintHeader from "../../components/common/CollegePrintHeader";

const REPORT_TYPES = [
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "yearly", label: "Yearly" },
  { value: "custom", label: "Custom Range" },
];

const TREND_SERIES = [
  { key: "present", source: "Present", label: "Present" },
  { key: "late", source: "Late", label: "Late" },
  { key: "halfDay", source: "Half Day", label: "Half Day" },
  { key: "onLeave", source: "On Leave", label: "On Leave" },
  { key: "medicalLeave", source: "Medical Leave", label: "Medical Leave" },
  { key: "absent", source: "Absent", label: "Absent" },
];

const getToday = () => new Date().toISOString().slice(0, 10);
const getMonthStart = () => {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1))
    .toISOString()
    .slice(0, 10);
};

const formatDisplayDate = (value = "") => {
  const [year, month, day] = value.split("-");
  if (!year || !month || !day) return value;
  return `${day}-${month}-${year}`;
};

export default function AttendanceReport() {
  const { academicYear } = useApp();
  const [searchParams] = useSearchParams();
  const paramTab = searchParams.get("tab");
  const paramCourse = searchParams.get("course") || "";
  const paramYear = searchParams.get("yearNumber") || "";
  const paramStudentId = searchParams.get("studentId") || "";

  const [activeTab, setActiveTab] = useState(paramTab || "daily"); // "daily" | "subject"
  const [reportType, setReportType] = useState("monthly");
  const [course, setCourse] = useState(paramCourse);
  const [yearNumber, setYearNumber] = useState(paramYear);
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState(getMonthStart);
  const [toDate, setToDate] = useState(getToday);
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [selectedStudentId, setSelectedStudentId] = useState(paramStudentId);
  const [courses, setCourses] = useState([]);

  const selectedCourse = useMemo(
    () => courses.find((c) => c._id === course),
    [courses, course],
  );

  const yearOptions = useMemo(() => {
    return selectedCourse ? getYearOptions(selectedCourse) : [];
  }, [selectedCourse]);

  useEffect(() => {
    courseService
      .getAll({ isActive: true })
      .then((res) => setCourses(res.data || []))
      .catch((err) => toast.error("Couldn't load courses: " + err.message));
  }, []);

  useEffect(() => {
    if (!academicYear?._id) return;

    setLoading(true);
    attendanceService
      .getReport({
        academicYear: academicYear._id,
        reportType,
        course,
        yearNumber,
        search,
        studentId: selectedStudentId,
        ...(reportType === "custom" ? { fromDate, toDate } : {}),
      })
      .then((res) => setReport(res.data))
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  }, [
    academicYear,
    reportType,
    course,
    yearNumber,
    search,
    selectedStudentId,
    fromDate,
    toDate,
  ]);

  const selectedStudent = useMemo(
    () =>
      (report?.studentSummaries || []).find(
        (entry) => entry.student._id === selectedStudentId,
      ) || null,
    [report, selectedStudentId],
  );

  const trendRows = useMemo(
    () =>
      (report?.trend || []).map((entry) =>
        TREND_SERIES.reduce(
          (acc, series) => ({
            ...acc,
            [series.key]: Number(entry?.[series.source] || 0),
          }),
          { date: entry.date },
        ),
      ),
    [report],
  );

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="attendance-print">
      <style>{`
        @media print {
          html, body {
            height: auto !important;
            overflow: visible !important;
            margin: 0 !important;
            padding: 0 !important;
            background: #fff !important;
          }

          body * {
            visibility: hidden !important;
          }

          .attendance-print,
          .attendance-print * {
            visibility: visible !important;
          }

          .attendance-print {
            position: static !important;
            width: 100% !important;
            padding: 0 !important;
            margin: 0 !important;
          }

          .app-layout,
          .main-content {
            display: block !important;
            margin: 0 !important;
            padding: 0 !important;
            width: 100% !important;
            min-height: auto !important;
          }

          .sidebar {
            display: none !important;
          }

          .no-print {
            display: none !important;
          }

          .print-block {
            display: block !important;
          }

          .print-table th,
          .print-table td {
            font-size: 11px !important;
            padding: 8px 10px !important;
          }

          .card {
            box-shadow: none !important;
            break-inside: avoid;
          }
        }
      `}</style>

      {/* ── Top Tabs ── */}
      <div className="no-print" style={{ display: "flex", gap: "0.5rem", marginBottom: "1.25rem", borderBottom: "1px solid var(--border)", paddingBottom: "0.65rem" }}>
        <button
          type="button"
          className={`btn ${activeTab === "daily" ? "btn-primary" : "btn-outline"}`}
          onClick={() => setActiveTab("daily")}
          style={{ fontSize: 13, display: "inline-flex", alignItems: "center", gap: "0.4rem", borderRadius: 8 }}
        >
          📅 Daily Attendance Report
        </button>
        <button
          type="button"
          className={`btn ${activeTab === "subject" ? "btn-primary" : "btn-outline"}`}
          onClick={() => setActiveTab("subject")}
          style={{ fontSize: 13, display: "inline-flex", alignItems: "center", gap: "0.4rem", borderRadius: 8 }}
        >
          📚 Subject-wise Attendance Report
        </button>
      </div>

      {activeTab === "subject" ? (
        <SubjectAttendanceReport />
      ) : (
        <>
          <div className="page-header no-print" style={{ flexWrap: "wrap", gap: "0.75rem" }}>
            <div>
              <h1 className="page-title">Daily Attendance Report</h1>
              <p className="page-subtitle">
                Review weekly, monthly, yearly, or custom daily attendance summaries.
              </p>
            </div>
            <button type="button" className="btn btn-primary" onClick={handlePrint}>
              Print Attendance Report
            </button>
          </div>

          {!academicYear ? (
            <div
              style={{
                background: "#fef3c7",
                border: "1px solid #fcd34d",
                borderRadius: 8,
                padding: "0.75rem 1rem",
                fontSize: 13,
                color: "#92400e",
              }}
            >
              Select an academic year from the Dashboard to view attendance reports.
            </div>
          ) : (
            <>
              <div className="card no-print" style={{ marginBottom: "1rem" }}>
            <div className="card-body">
              <div className="form-grid" style={{ alignItems: "end" }}>
                <div className="form-group">
                  <label className="form-label">Report Type</label>
                  <select
                    className="form-control"
                    value={reportType}
                    onChange={(e) => setReportType(e.target.value)}
                  >
                    {REPORT_TYPES.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Course</label>
                  <select
                    className="form-control"
                    value={course}
                    onChange={(e) => {
                      setCourse(e.target.value);
                      setYearNumber("");
                    }}
                  >
                    <option value="">All Courses</option>
                    {courses.map((c) => (
                      <option key={c._id} value={c._id}>
                        {c.shortName}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Class Year</label>
                  <select
                    className="form-control"
                    value={yearNumber}
                    onChange={(e) => setYearNumber(e.target.value)}
                    disabled={!course}
                  >
                    <option value="">All Years</option>
                    {yearOptions.map((year) => (
                      <option key={year.yearNumber} value={year.yearNumber}>
                        {year.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Student Search</label>
                  <input
                    className="form-control"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Name or enrollment..."
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Student Focus</label>
                  <select
                    className="form-control"
                    value={selectedStudentId}
                    onChange={(e) => setSelectedStudentId(e.target.value)}
                  >
                    <option value="">All Students</option>
                    {(report?.studentSummaries || []).map((entry) => (
                      <option key={entry.student._id} value={entry.student._id}>
                        {entry.student.fullName} ({entry.student.enrollmentNo})
                      </option>
                    ))}
                  </select>
                </div>

                {reportType === "custom" && (
                  <>
                    <div className="form-group">
                      <label className="form-label">From Date</label>
                      <input
                        className="form-control"
                        type="date"
                        value={fromDate}
                        onChange={(e) => setFromDate(e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">To Date</label>
                      <input
                        className="form-control"
                        type="date"
                        value={toDate}
                        onChange={(e) => setToDate(e.target.value)}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="only-print" style={{ marginBottom: "1.25rem" }}>
            <CollegePrintHeader
              layout="banner"
              logoSize={58}
              title="STUDENT ATTENDANCE REPORT"
              subtitle={`Academic Year: ${academicYear?.label || "—"} · ${REPORT_TYPES.find((item) => item.value === reportType)?.label || reportType} Report`}
              rightContent={
                <div style={{ fontSize: "11px", color: "#475569" }}>
                  <div>Course: <strong>{selectedCourse?.shortName || "All Courses"}</strong></div>
                  <div>Period: <strong>{report?.range ? `${formatDisplayDate(report.range.fromDate)} to ${formatDisplayDate(report.range.toDate)}` : "-"}</strong></div>
                </div>
              }
            />
          </div>

          {loading ? (
            <div className="card">
              <div className="loading-center">
                <div className="spinner" />
                Loading attendance report...
              </div>
            </div>
          ) : (
            <>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                  gap: "0.75rem",
                  marginBottom: "1rem",
                }}
              >
                <div className="card">
                  <div className="card-body" style={{ padding: "1rem" }}>
                    <div style={{ fontSize: 12, color: "var(--text-muted)" }}>Working Days</div>
                    <div style={{ fontSize: 24, fontWeight: 700 }}>
                      {report?.summary?.workingDays || 0}
                    </div>
                  </div>
                </div>

                <div className="card">
                  <div className="card-body" style={{ padding: "1rem" }}>
                    <div style={{ fontSize: 12, color: "var(--text-muted)" }}>Students</div>
                    <div style={{ fontSize: 24, fontWeight: 700 }}>
                      {report?.summary?.totalStudents || 0}
                    </div>
                  </div>
                </div>

                <div className="card">
                  <div className="card-body" style={{ padding: "1rem" }}>
                    <div style={{ fontSize: 12, color: "var(--text-muted)" }}>Marked Entries</div>
                    <div style={{ fontSize: 24, fontWeight: 700 }}>
                      {report?.summary?.totalMarked || 0}
                    </div>
                  </div>
                </div>

                <div className="card">
                  <div className="card-body" style={{ padding: "1rem" }}>
                    <div style={{ fontSize: 12, color: "var(--text-muted)" }}>Present %</div>
                    <div style={{ fontSize: 24, fontWeight: 700, color: "#15803d" }}>
                      {report?.summary?.presentPercentage || 0}%
                    </div>
                  </div>
                </div>
              </div>

              <div className="card" style={{ marginBottom: "1rem" }}>
                <div className="card-header">
                  {selectedStudent ? "Individual Attendance Report" : "Attendance Trends"}
                  {report?.range && (
                    <span
                      style={{
                        marginLeft: 8,
                        fontWeight: 400,
                        color: "var(--text-muted)",
                        fontSize: 12,
                      }}
                    >
                      {formatDisplayDate(report.range.fromDate)} to {formatDisplayDate(report.range.toDate)}
                    </span>
                  )}
                </div>
                <div className="card-body">
                  {selectedStudent ? (
                    !selectedStudent.entries?.length ? (
                      <div
                        style={{
                          padding: "2rem 0",
                          textAlign: "center",
                          color: "var(--text-muted)",
                        }}
                      >
                        No attendance entries found for the selected student.
                      </div>
                    ) : (
                      <div className="table-wrapper">
                        <table className="print-table">
                          <thead>
                            <tr>
                              <th>Date</th>
                              <th>Status</th>
                              <th>Remarks</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedStudent.entries.map((entry, index) => (
                              <tr key={`${entry.attendanceDate}-${entry.status}-${index}`}>
                                <td>{formatDisplayDate(entry.attendanceDate)}</td>
                                <td>{entry.status}</td>
                                <td>{entry.remarks || "-"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )
                  ) : !trendRows.length ? (
                    <div
                      style={{
                        padding: "2rem 0",
                        textAlign: "center",
                        color: "var(--text-muted)",
                      }}
                    >
                      No attendance records found for the selected range.
                    </div>
                  ) : (
                    <div className="table-wrapper">
                      <table className="print-table">
                        <thead>
                          <tr>
                            <th>Date</th>
                            {TREND_SERIES.map((series) => (
                              <th key={series.key}>{series.label}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {trendRows.map((entry) => (
                            <tr key={entry.date}>
                              <td>{formatDisplayDate(entry.date)}</td>
                              {TREND_SERIES.map((series) => (
                                <td key={series.key}>{entry[series.key]}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>

              <div className="card">
                <div className="card-header">Student Attendance Summary</div>
                {!report?.studentSummaries?.length ? (
                  <div className="empty-state">
                    <p>No student-level attendance data found.</p>
                  </div>
                ) : (
                  <div className="table-wrapper">
                    <table className="print-table">
                      <thead>
                        <tr>
                          <th>Student</th>
                          <th>Enrollment</th>
                          <th>Total Days</th>
                          <th>Present</th>
                          <th>Absent</th>
                          <th>Leave</th>
                          <th>Late</th>
                          <th>Attendance %</th>
                        </tr>
                      </thead>
                      <tbody>
                        {report.studentSummaries.map((entry) => (
                          <tr
                            key={entry.student._id}
                            onClick={() => setSelectedStudentId(entry.student._id)}
                            style={{
                              cursor: "pointer",
                              background:
                                entry.student._id === selectedStudentId ? "#e8f1f8" : undefined,
                            }}
                          >
                            <td>
                              <div style={{ fontWeight: 600 }}>{entry.student.fullName}</div>
                              <div style={{ fontSize: 11.5, color: "var(--text-muted)" }}>
                                {entry.student.course?.shortName || entry.student.course?.courseName || "Course"} {entry.student.yearNumber ? `· Year ${entry.student.yearNumber}` : ""}
                              </div>
                            </td>
                            <td>{entry.student.enrollmentNo}</td>
                            <td>{entry.totalDays}</td>
                            <td>{entry.presentDays}</td>
                            <td>{entry.absentDays}</td>
                            <td>{entry.leaveDays}</td>
                            <td>{entry.lateDays}</td>
                            <td>
                              <span
                                className="badge"
                                style={{
                                  background:
                                    entry.attendancePercentage >= 75 ? "#dcfce7" : "#fee2e2",
                                  color:
                                    entry.attendancePercentage >= 75 ? "#166534" : "#b91c1c",
                                }}
                              >
                                {entry.attendancePercentage}%
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </>
      )}
        </>
      )}
    </div>
  );
}
